/**
 * Message templates screen (mobile C6).
 *
 * Lists reusable email/SMS templates (GET /api/message-templates) and offers an inline
 * "+ New template" form (name, channel, subject [email], body, optional purpose) →
 * POST /api/message-templates.
 *
 * Pushed over the tabs on the parent (app) stack. NEVER imports @smartbox/* (invariant 1).
 * Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTemplates,
  createTemplate,
  type Channel,
  type Purpose,
  type TemplateRow,
} from '../../../src/api/comms';
import { Screen, Text, Card, Button, Field, Select, Badge, ListRow } from '../../../src/components/ui';
import { theme, type StatusKey } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

const CHANNEL_OPTIONS: ReadonlyArray<{ value: Channel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const PURPOSE_OPTIONS: ReadonlyArray<{ value: Purpose; label: string }> = [
  { value: 'generic', label: 'Generic' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'estimate_followup', label: 'Estimate follow-up' },
  { value: 'next_phase_follow_up', label: 'Next phase follow-up' },
];

function channelBadge(channel: string): { key: StatusKey; label: string } {
  return channel === 'sms'
    ? { key: 'inProgress', label: 'SMS' }
    : { key: 'sent', label: 'Email' };
}

export default function TemplatesScreen() {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<Channel>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [purpose, setPurpose] = useState<Purpose | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['message-templates'],
    queryFn: listTemplates,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createTemplate({
        name: name.trim(),
        channel,
        body: body.trim(),
        ...(channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
        ...(purpose ? { purpose } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['message-templates'] });
      setShowForm(false);
      setName('');
      setSubject('');
      setBody('');
      setPurpose(null);
      setFormError(null);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create template.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('Enter a template name.');
      return;
    }
    if (!body.trim()) {
      setFormError('Enter the template body.');
      return;
    }
    createMutation.mutate();
  };

  const templates = templatesQuery.data ?? [];
  const isEmail = channel === 'email';

  return (
    <Screen scroll>
      <Text variant="title">Templates</Text>

      <Button
        title={showForm ? 'Cancel' : '+ New template'}
        variant={showForm ? 'secondary' : 'primary'}
        onPress={() => setShowForm((v) => !v)}
        accessibilityLabel="New template"
      />

      {showForm ? (
        <Card>
          <Text variant="bodyStrong">New template</Text>
          <Field
            label="Name"
            placeholder="e.g. Estimate ready"
            value={name}
            onChangeText={setName}
            accessibilityLabel="Template name"
          />
          <Select label="Channel" options={CHANNEL_OPTIONS} value={channel} onChange={setChannel} />
          {isEmail ? (
            <Field
              label="Subject (optional)"
              placeholder="e.g. Your estimate is ready"
              value={subject}
              onChangeText={setSubject}
              accessibilityLabel="Template subject"
            />
          ) : null}
          <Field
            label="Body"
            placeholder="Message text…"
            value={body}
            onChangeText={setBody}
            multiline
            numberOfLines={6}
            style={styles.multiline}
            accessibilityLabel="Template body"
          />
          <Select
            label="Purpose (optional)"
            options={PURPOSE_OPTIONS}
            value={purpose}
            onChange={setPurpose}
          />
          {formError ? (
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          ) : null}
          <Button
            title="Save template"
            onPress={onCreate}
            loading={createMutation.isPending}
            disabled={createMutation.isPending}
            accessibilityLabel="Save template"
          />
        </Card>
      ) : null}

      <Text variant="label" color="secondary">
        {templates.length} {templates.length === 1 ? 'template' : 'templates'}
      </Text>

      {templatesQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading templates" />
      ) : templatesQuery.isError ? (
        <Card>
          <Text variant="body" color="danger" accessibilityRole="alert">
            {extractErrorMessage(templatesQuery.error, 'Failed to load templates.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => templatesQuery.refetch()}
          />
        </Card>
      ) : templates.length === 0 ? (
        <Text variant="body" color="secondary">
          No templates yet. Tap “New template” to add your first.
        </Text>
      ) : (
        <View style={styles.list}>
          {templates.map((t: TemplateRow) => {
            const b = channelBadge(t.channel);
            const subtitle = t.purpose ? humanize(t.purpose) : t.subject ?? 'No subject';
            return (
              <ListRow
                key={t.id}
                title={t.name}
                subtitle={subtitle}
                right={<Badge status={b.key} label={b.label} />}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
  multiline: {
    minHeight: theme.layout.fieldHeight * 3,
    textAlignVertical: 'top',
  },
});
